import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { getStorageItem, setStorageItem, removeStorageItem } from "~/lib/storage/Storage";

interface QuizState {
    surveyData: Record<string, unknown>;
    pageNo: number;
}

interface States {
    quizzes: Record<string, QuizState>;
}

interface Actions {
    setSurvey: (category: string, survey: { data: Record<string, unknown>, pageNo: number }) => void;
    setSurveyData: (category: string, surveyData: Record<string, unknown>) => void;
    setPageNo: (category: string, pageNo: number) => void;
    getQuiz: (category: string) => QuizState | undefined;
    reset: (category?: string) => void;
    // Legacy methods for backward compatibility (use default category "")
    setSurveyLegacy: (survey: { data: Record<string, unknown>, pageNo: number }) => void;
    setSurveyDataLegacy: (surveyData: Record<string, unknown>) => void;
    setPageNoLegacy: (pageNo: number) => void;
}

const quizStorage = {
    getItem: (name: string): string | null => {
        // Guard against server-side rendering
        if (typeof window === "undefined") return null;

        try {
            return getStorageItem(name);
        } catch (error) {
            console.error("Error reading quiz storage:", error);
            return null;
        }
    },

    setItem: (name: string, value: string): void => {
        // Guard against server-side rendering
        if (typeof window === "undefined") return;

        try {
            setStorageItem(name, value);
        } catch (error) {
            console.error("Error writing quiz storage:", error);
        }
    },
    
    removeItem: (name: string): void => {
        // Guard against server-side rendering
        if (typeof window === "undefined") return;

        removeStorageItem(name);
    },
};

const DEFAULT_CATEGORY = "";

const getInitialState = (): States => ({
    quizzes: {},
});

export const useQuizStore = create<States & Actions>()(
    persist(
        (set, get) => ({
            ...getInitialState(),
            
            setSurvey: (category, survey) => {
                const state = get();
                set({
                    quizzes: {
                        ...state.quizzes,
                        [category]: {
                            surveyData: survey.data,
                            pageNo: survey.pageNo,
                        },
                    },
                });
            },
            
            setSurveyData: (category, surveyData) => {
                const state = get();
                const currentQuiz = state.quizzes[category] || { surveyData: {}, pageNo: 0 };
                set({
                    quizzes: {
                        ...state.quizzes,
                        [category]: {
                            ...currentQuiz,
                            surveyData,
                        },
                    },
                });
            },
            
            setPageNo: (category, pageNo) => {
                const state = get();
                const currentQuiz = state.quizzes[category] || { surveyData: {}, pageNo: 0 };
                set({
                    quizzes: {
                        ...state.quizzes,
                        [category]: {
                            ...currentQuiz,
                            pageNo,
                        },
                    },
                });
            },
            
            getQuiz: (category) => {
                const state = get();
                return state.quizzes[category];
            },
            
            reset: (category) => {
                if (category !== undefined) {
                    // Reset specific quiz
                    const state = get();
                    const newQuizzes = { ...state.quizzes };
                    delete newQuizzes[category];
                    set({ quizzes: newQuizzes });
                } else {
                    // Reset all quizzes
                    set(getInitialState());
                }
            },
            
            // Legacy methods for backward compatibility
            setSurveyLegacy: (survey) => {
                const state = get();
                set({
                    quizzes: {
                        ...state.quizzes,
                        [DEFAULT_CATEGORY]: {
                            surveyData: survey.data,
                            pageNo: survey.pageNo,
                        },
                    },
                });
            },
            
            setSurveyDataLegacy: (surveyData) => {
                const state = get();
                const currentQuiz = state.quizzes[DEFAULT_CATEGORY] || { surveyData: {}, pageNo: 0 };
                set({
                    quizzes: {
                        ...state.quizzes,
                        [DEFAULT_CATEGORY]: {
                            ...currentQuiz,
                            surveyData,
                        },
                    },
                });
            },
            
            setPageNoLegacy: (pageNo) => {
                const state = get();
                const currentQuiz = state.quizzes[DEFAULT_CATEGORY] || { surveyData: {}, pageNo: 0 };
                set({
                    quizzes: {
                        ...state.quizzes,
                        [DEFAULT_CATEGORY]: {
                            ...currentQuiz,
                            pageNo,
                        },
                    },
                });
            },
        }),
        {
            name: "quiz-store",
            storage: createJSONStorage(() => quizStorage),
            version: 1,
            // Migration: convert old format to new format
            migrate: (persistedState: any) => {
                if (!persistedState) return getInitialState();
                
                // If it's already in new format, return as is
                if (persistedState.quizzes) {
                    return persistedState;
                }
                
                // Migrate from old format to new format
                const oldState = persistedState as { surveyData?: any; pageNo?: number };
                return {
                    quizzes: {
                        [DEFAULT_CATEGORY]: {
                            surveyData: oldState.surveyData || {},
                            pageNo: oldState.pageNo || 0,
                        },
                    },
                };
            },
        }
    )
);